// Settings tab — the install's own settings (`GET`/`PUT /api/v1/settings`):
// its name (printed in the signing stamp), the upload limits the upload path
// enforces the moment they are saved, and the group grants — which of docs'
// presets or scopes each zehn-id group of the install gets. A person holds,
// on every request, the union of their groups' grants. The default
// visibility of new files and the signing stamp are in the Policies tab.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2, Plus, Save, Settings2, ShieldCheck, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FieldError } from '@/shared/ui/FieldError.jsx';
import { fieldErrors } from '@/shared/utils/fieldErrors.js';
import { api, ep } from '../lib/api.js';
import { isScopeGrant, presetLabel, scopeGroupOf, useScopeCatalogue } from '../lib/scopeCatalogue.js';

// docs-api's limits (app/modules/platform/schemas/auth.py SettingsUpdate,
// app/modules/settings/limits.py): 0–2048 MB (0 = no limit; stored, read and
// saved alike), at most 50 extensions of letters/digits/_ up to 10
// characters, 500 characters joined; a name of at most 255 characters.
const SIZE_MIN = 0;
const SIZE_MAX = 2048;
const TYPES_MAX = 50;
const TYPES_CHARS = 500;
const NAME_MAX = 255;
const EXTENSION = /^[a-z0-9_]{1,10}$/;
// docs-api's key for "every signed-in person" in the group grants.
const EVERYONE = '*';

const sameList = (a, b) => (a || []).join(',') === (b || []).join(',');

function generalProblems(draft, saved, t) {
  const out = {};
  // Only a changed size is checked (what docs-api stored is valid).
  if (String(draft.max_file_size_mb) !== String(saved.max_file_size_mb)) {
    const size = Number(draft.max_file_size_mb);
    if (draft.max_file_size_mb === '' || !Number.isInteger(size) || size < SIZE_MIN || size > SIZE_MAX) {
      out.max_file_size_mb = t('settings.general.sizeRange');
    }
  }
  const types = draft.allowed_file_types || [];
  if (types.length > TYPES_MAX) out.allowed_file_types = t('errors:fields.tooMany', { max: TYPES_MAX });
  else if (types.join(',').length > TYPES_CHARS) out.allowed_file_types = t('errors:fields.tooLong', { max: TYPES_CHARS });
  if ((draft.organization_name || '').trim().length > NAME_MAX) out.organization_name = t('errors:fields.tooLong', { max: NAME_MAX });
  return out;
}

function GeneralSection({ settings, onSaved }) {
  const { t } = useTranslation('console');
  const [draft, setDraft] = useState(() => ({
    organization_name: settings.organization_name || '',
    max_file_size_mb: settings.max_file_size_mb,
    allowed_file_types: [...(settings.allowed_file_types || [])],
  }));
  const [extInput, setExtInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [typeError, setTypeError] = useState(null);
  const [serverErrors, setServerErrors] = useState({});

  const changes = {};
  if (draft.organization_name.trim() !== (settings.organization_name || '')) changes.organization_name = draft.organization_name.trim();
  if (String(draft.max_file_size_mb) !== String(settings.max_file_size_mb)) changes.max_file_size_mb = Number(draft.max_file_size_mb);
  if (!sameList(draft.allowed_file_types, settings.allowed_file_types)) changes.allowed_file_types = draft.allowed_file_types;
  const dirty = Object.keys(changes).length > 0;

  const addExtensions = (raw) => {
    const parts = raw.split(/[,\s]+/).map((p) => p.trim().toLowerCase().replace(/^\./, '')).filter(Boolean);
    if (parts.length === 0) return;
    // Keep what isn't an extension in the box, with a word on why.
    const bad = parts.filter((p) => !EXTENSION.test(p));
    const good = parts.filter((p) => EXTENSION.test(p));
    setDraft((prev) => ({ ...prev, allowed_file_types: Array.from(new Set([...prev.allowed_file_types, ...good])) }));
    setExtInput(bad.join(', '));
    setTypeError(bad.length ? t('settings.general.badType', { ext: bad.join(', ') }) : null);
    setServerErrors({});
  };
  const removeExtension = (ext) => setDraft((prev) => ({ ...prev, allowed_file_types: prev.allowed_file_types.filter((e) => e !== ext) }));

  const problems = generalProblems(draft, settings, t);
  const errors = { ...problems, ...serverErrors };

  const save = async () => {
    if (Object.keys(problems).length || !dirty) return;
    setSaving(true);
    const res = await api.put(ep.settings(), { data: changes });
    setSaving(false);
    if (res.success) {
      setDraft({
        organization_name: res.response.organization_name || '',
        max_file_size_mb: res.response.max_file_size_mb,
        allowed_file_types: [...(res.response.allowed_file_types || [])],
      });
      onSaved(res.response);
      toast.success(t('settings.general.saved'));
    } else {
      const placed = fieldErrors(res);
      setServerErrors(placed);
      if (!placed.max_file_size_mb && !placed.allowed_file_types && !placed.organization_name) toast.error(res.message);
    }
  };

  return (
    <section className="space-y-5" data-testid="settings-general">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Settings2 className="h-5 w-5 text-muted-foreground" /> {t('settings.general.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('settings.general.description')}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="organization-name" className="flex items-center gap-1.5">
          <Building2 className="h-4 w-4 text-muted-foreground" /> {t('settings.general.organizationName')}
        </Label>
        <Input
          id="organization-name"
          value={draft.organization_name}
          maxLength={NAME_MAX}
          aria-invalid={errors.organization_name ? true : undefined}
          aria-describedby={errors.organization_name ? 'organization-name-error' : undefined}
          onChange={(e) => { setDraft({ ...draft, organization_name: e.target.value }); setServerErrors({}); }}
        />
        <FieldError id="organization-name-error" message={errors.organization_name} />
        <p className="text-xs text-muted-foreground">{t('settings.general.organizationNameHint')}</p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="max-file-size">{t('settings.general.maxFileSize')}</Label>
          <Input
            id="max-file-size"
            type="number"
            min={SIZE_MIN}
            max={SIZE_MAX}
            step={1}
            required
            value={draft.max_file_size_mb}
            aria-invalid={errors.max_file_size_mb ? true : undefined}
            aria-describedby={errors.max_file_size_mb ? 'max-file-size-error' : undefined}
            onChange={(e) => { setDraft({ ...draft, max_file_size_mb: e.target.value }); setServerErrors({}); }}
          />
          <FieldError id="max-file-size-error" message={errors.max_file_size_mb} />
          <p className="text-xs text-muted-foreground">{t('settings.general.maxFileSizeHint')}</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="allowed-types">{t('settings.general.allowedTypes')}</Label>
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border bg-card p-2">
          {draft.allowed_file_types.map((ext) => (
            <span key={ext} className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs font-mono">
              .{ext}
              <button type="button" onClick={() => removeExtension(ext)} className="text-muted-foreground hover:text-foreground" aria-label={t('settings.general.removeType', { ext })}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            id="allowed-types"
            value={extInput}
            maxLength={TYPES_CHARS}
            aria-invalid={typeError || errors.allowed_file_types ? true : undefined}
            aria-describedby="allowed-types-error"
            onChange={(e) => { setExtInput(e.target.value); setTypeError(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); addExtensions(extInput); }
              if (e.key === 'Backspace' && !extInput && draft.allowed_file_types.length) removeExtension(draft.allowed_file_types[draft.allowed_file_types.length - 1]);
            }}
            onBlur={() => addExtensions(extInput)}
            placeholder={draft.allowed_file_types.length ? '' : t('settings.general.allowedTypesPlaceholder')}
            className="min-w-32 flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        <FieldError id="allowed-types-error" message={typeError || errors.allowed_file_types} />
        <p className="text-xs text-muted-foreground">{t('settings.general.allowedTypesHint')}</p>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || !dirty || Object.keys(problems).length > 0} className="gap-2" data-testid="settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('settings.general.save')}
        </Button>
      </div>
    </section>
  );
}

// ── group grants ──────────────────────────────────────────────────────────

const rowsOf = (grants) => Object.entries(grants || {}).map(([group, list]) => ({ group, grants: [...list] }));
const grantsOf = (rows) => Object.fromEntries(rows.map((r) => [r.group.trim(), r.grants]));
const byKey = ([x], [y]) => x.localeCompare(y);
const sameGrants = (a, b) => JSON.stringify(Object.entries(a).sort(byKey)) === JSON.stringify(Object.entries(b || {}).sort(byKey));

function grantProblems(rows, t) {
  const out = {};
  const seen = new Set();
  rows.forEach((row, i) => {
    const name = row.group.trim();
    if (!name) out[i] = t('errors:fields.required');
    else if (seen.has(name)) out[i] = t('settings.grants.duplicate');
    seen.add(name);
  });
  return out;
}

function GrantChip({ grant, catalogue, unknown, onRemove }) {
  const { t } = useTranslation('console');
  const scope = isScopeGrant(grant);
  const info = scope ? catalogue?.scopes.find((s) => s.scope === grant) : null;
  const preset = !scope ? catalogue?.presets[grant] : null;
  const title = scope ? info?.description : preset?.join(', ');
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
        unknown ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' : 'bg-muted/50 text-foreground'
      } ${scope ? 'font-mono' : 'font-medium'}`}
      title={title || undefined}
      data-grant={grant}
    >
      {scope ? grant : presetLabel(t, grant)}
      <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-foreground" aria-label={t('settings.grants.removeGrant', { grant })}>
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function GrantPicker({ row, index, catalogue, onAdd }) {
  const { t } = useTranslation('console');
  const presets = Object.keys(catalogue?.presets || {}).filter((p) => !row.grants.includes(p));
  const byCategory = useMemo(() => {
    const out = {};
    (catalogue?.scopes || []).forEach((s) => {
      if (row.grants.includes(s.scope)) return;
      (out[scopeGroupOf(s)] ||= []).push(s);
    });
    return out;
  }, [catalogue, row.grants]);
  if (!catalogue) return null;
  return (
    <Select value="" onValueChange={(grant) => grant && onAdd(grant)}>
      <SelectTrigger className="h-7 w-40 text-xs" aria-label={t('settings.grants.addGrantFor', { group: row.group || `#${index + 1}` })}>
        <SelectValue placeholder={t('settings.grants.addGrant')} />
      </SelectTrigger>
      <SelectContent>
        {presets.length > 0 && (
          <SelectGroup>
            <SelectLabel>{t('settings.grants.presets')}</SelectLabel>
            {presets.map((p) => (
              <SelectItem key={p} value={p} title={catalogue.presets[p].join(', ')}>{presetLabel(t, p)}</SelectItem>
            ))}
          </SelectGroup>
        )}
        {Object.entries(byCategory).map(([category, scopes]) => (
          <SelectGroup key={category}>
            <SelectLabel>{t(`scopes.categories.${category}`, { defaultValue: category })}</SelectLabel>
            {scopes.map((s) => (
              <SelectItem key={s.scope} value={s.scope} title={s.description} className="font-mono text-xs">{s.scope}</SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

function GroupGrantsSection({ settings, onSaved }) {
  const { t } = useTranslation('console');
  const { catalogue, error: catalogueError } = useScopeCatalogue();
  const [rows, setRows] = useState(() => rowsOf(settings.group_grants));
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [unknown, setUnknown] = useState([]);
  // Stable keys for rows whose group name is being typed.
  const nextKey = useRef(0);
  const [keys, setKeys] = useState(() => rows.map(() => nextKey.current++));

  const problems = grantProblems(rows, t);
  const blocked = Object.keys(problems).length > 0;
  const dirty = !sameGrants(grantsOf(rows), settings.group_grants);
  // Grants the catalogue does not name (retired, or refused by the server).
  const isUnknown = (grant) =>
    unknown.includes(grant) ||
    (catalogue && (isScopeGrant(grant) ? !catalogue.scopes.some((s) => s.scope === grant) : !(grant in catalogue.presets)));

  const update = (i, next) => {
    setRows((prev) => prev.map((row, j) => (j === i ? { ...row, ...next } : row)));
    setUnknown([]);
  };
  const addGroup = () => {
    setRows((prev) => [...prev, { group: '', grants: [] }]);
    setKeys((prev) => [...prev, nextKey.current++]);
  };
  const removeGroup = (i) => {
    setRows((prev) => prev.filter((_, j) => j !== i));
    setKeys((prev) => prev.filter((_, j) => j !== i));
  };

  const save = async () => {
    setTried(true);
    if (blocked || !dirty) return;
    setSaving(true);
    const res = await api.put(ep.settings(), { data: { group_grants: grantsOf(rows) } });
    setSaving(false);
    if (res.success) {
      const next = rowsOf(res.response.group_grants);
      setRows(next);
      setKeys(next.map(() => nextKey.current++));
      setTried(false);
      onSaved(res.response);
      toast.success(t('settings.grants.saved'));
    } else {
      if (res.errorInfo?.code === 'unknown_grants') setUnknown(res.errorInfo.params?.unknown || []);
      toast.error(res.message);
    }
  };

  return (
    <section className="space-y-4" data-testid="settings-grants">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" /> {t('settings.grants.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('settings.grants.description')}</p>
        <p className="text-xs text-muted-foreground mt-1">{t('settings.grants.everyoneHint', { key: EVERYONE })}</p>
      </div>

      {catalogueError && <p className="text-sm text-red-600 dark:text-red-400">{catalogueError}</p>}

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">{t('settings.grants.empty')}</p>
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {rows.map((row, i) => {
            const problem = tried || row.group ? problems[i] : null;
            return (
              <li key={keys[i]} className="flex flex-wrap items-start gap-3 p-3" data-testid={`grant-row-${i}`}>
                <div className="w-48 max-w-full space-y-1">
                  <Input
                    value={row.group}
                    className="h-8"
                    placeholder={t('settings.grants.groupPlaceholder')}
                    aria-label={t('settings.grants.group')}
                    aria-invalid={problem ? true : undefined}
                    aria-describedby={problem ? `grant-group-${i}-error` : undefined}
                    onChange={(e) => update(i, { group: e.target.value })}
                  />
                  <FieldError id={`grant-group-${i}-error`} message={problem} />
                </div>
                <div className="flex flex-1 flex-wrap items-center gap-1.5 min-w-56">
                  {row.grants.map((grant) => (
                    <GrantChip
                      key={grant}
                      grant={grant}
                      catalogue={catalogue}
                      unknown={isUnknown(grant)}
                      onRemove={() => update(i, { grants: row.grants.filter((g) => g !== grant) })}
                    />
                  ))}
                  {row.grants.length === 0 && <span className="text-xs text-muted-foreground">{t('settings.grants.none')}</span>}
                  <GrantPicker row={row} index={i} catalogue={catalogue} onAdd={(grant) => update(i, { grants: [...row.grants, grant] })} />
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeGroup(i)} aria-label={t('settings.grants.removeGroup', { group: row.group })}>
                  <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" onClick={addGroup} className="gap-2" data-testid="grant-add-group">
          <Plus className="w-4 h-4" /> {t('settings.grants.addGroup')}
        </Button>
        <Button onClick={save} disabled={saving || !dirty || (tried && blocked)} className="gap-2" data-testid="grants-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('settings.grants.save')}
        </Button>
      </div>
    </section>
  );
}

export const SettingsTab = ({ onSettingsSaved }) => {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(ep.settings()).then((res) => {
      if (cancelled) return;
      if (res.success) setSettings(res.response);
      else setError(res.message);
    });
    return () => { cancelled = true; };
  }, []);

  const saved = (next) => {
    setSettings(next);
    onSettingsSaved?.(next);
  };

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!settings) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  return (
    <div className="space-y-10 max-w-3xl">
      <GroupGrantsSection settings={settings} onSaved={saved} />
      <hr />
      <GeneralSection settings={settings} onSaved={saved} />
    </div>
  );
};
